import { useRef, useState } from "react";
import { useGreyFleetAlerts } from "../../../api/admin/greyFleet";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { useDownloadComponentToPdf } from "../../../hooks/useDownloadComponentToPdf";
import useDriversPage from "./useDriversPage";
import { errorToast, successToast } from "../../../toast";
import { downloadBlobAsFile } from "../../../utils/downloadBlobAsFile";
import { useDownloadGreyFleet } from "../../../api/greyFleet";
import { GreyFleetReportTypes } from "../../../models/api/reporting";
import {
    tableHeaderOptionsEnum,
    useTableHeaders,
} from "../../../hooks/useTableHeaders";
import useTableStore from "../../../stores/useTableStore";
import { driverCategoryOptionsEn } from "../../../utils/driverPageLocation";
import { useLocation } from "react-router-dom";

const useAdminGreyFleet = () => {
    const location = useLocation();
    const {
        filterModalOpen,
        setFilterModalOpen,
        driversList,
        fetchDriversError,
        paginationState,
        handleFetchDrivers,
        isFetchingDrivers,
        employmentFilters,
        constructAllDriversBodyForReport,
        query,
        setQuery,
    } = useDriversPage(location.pathname);

    const [isReportDownloading, setIsReportDownloading] = useState(false);
    const isRedirect = useTableStore((state) => state.isRedirect);
    const getAppliedFilter = useTableStore((state) => state.getAppliedFilter);
    const appliedFilter = getAppliedFilter(driverCategoryOptionsEn.greyfleet);

    const { pageNumber, setPageNumber, pageSize, setPageSize } =
        paginationState;

    const { selectedCompany, selectedDepartment } = employmentFilters;

    const {
        data: greyFleetAlertsData,
        isLoading: isLoadingGreyFleetAlertsData,
        error: greyFleetAlertsError,
    } = useGreyFleetAlerts(
        selectedCompany?.companyId,
        selectedDepartment?.departmentId
    );

    const { setTableHeaders, setHiddenCols } = useTableHeaders(
        tableHeaderOptionsEnum.greyFleet,
        !isRedirect
    );
    const tableHeaders = useTableStore((state) => state.tableHeadersGreyFleet);
    const hiddenCols = useTableStore((state) => state.hiddenColsGreyFleet);

    const [isVehicleAnalysisOpen, setIsVehicleAnalysisOpen] =
        useLocalStorage<boolean>("isGreyFleetVehicleAnalysisOpen", true);
    const [isGreyFleetDriversOpen, setIsGreyFleetDriversOpen] =
        useLocalStorage<boolean>("isGreyFleetDriversOpen", true);

    // PDF report:
    const reportRef = useRef<HTMLDivElement | null>(null);
    const { generatePDF } = useDownloadComponentToPdf(reportRef);
    const [showReportView, setShowReportView] = useState(false);

    const handleDownloadPdfReport = (): void => {
        setShowReportView(true);

        const fileNameArray = [
            selectedCompany?.companyName ?? "All clients",
            selectedDepartment?.departmentName ?? "All departments",
            "Grey Fleet report",
        ];
        generatePDF(() => setShowReportView(false), fileNameArray.join("-"));
    };

    const { getGreyFleetReport } = useDownloadGreyFleet();

    const handleDownloadReport = async (
        reportFormatType: GreyFleetReportTypes
    ) => {
        setIsReportDownloading(true);

        const fileExtension =
            reportFormatType === GreyFleetReportTypes.XLS
                ? "xlsx"
                : reportFormatType.toLowerCase();
        const fileNameArray = [`GreyFleetReport.${fileExtension}`];
        if (selectedDepartment)
            fileNameArray.unshift(selectedDepartment.departmentName);
        if (selectedCompany) fileNameArray.unshift(selectedCompany.companyName);
        const fileName = fileNameArray.join("-");

        try {
            const getAllDriversBody = constructAllDriversBodyForReport(
                appliedFilter.filterFields,
                reportFormatType,
                selectedCompany?.companyId,
                selectedDepartment?.departmentId
            );
            const response = await getGreyFleetReport(getAllDriversBody);
            if (response.success) {
                downloadBlobAsFile(response.content, fileName);
                successToast(`Successfully downloaded Grey Fleet report`);
                setTimeout(() => setIsReportDownloading(false), 1000);
            }
        } catch (error) {
            setTimeout(() => setIsReportDownloading(false), 1000);
            errorToast(`Failed to download Grey Fleet report`);
        }
    };

    return {
        greyFleetAlertsData,
        isLoadingGreyFleetAlertsData,
        greyFleetAlertsError,
        greyFleetDriversList: driversList,
        greyFleetdriversListError: fetchDriversError,
        isGettingGreyFleetDriversList: isFetchingDrivers,
        handleFetchDrivers,
        pageNumber,
        setPageNumber,
        pageSize,
        setPageSize,
        tableHeaders,
        setTableHeaders,
        hiddenCols,
        setHiddenCols,
        isVehicleAnalysisOpen,
        setIsVehicleAnalysisOpen,
        isGreyFleetDriversOpen,
        setIsGreyFleetDriversOpen,
        employmentFilters,
        handleDownloadPdfReport,
        showReportView,
        reportRef,
        filterModalOpen,
        setFilterModalOpen,
        query,
        setQuery,
        isReportDownloading,
        handleDownloadReport,
    };
};

export default useAdminGreyFleet;
